// Shown instead of the Active page while a workspace is past_due — a renewal
// charge was declined and the grace period is running. Access continues until
// grace ends; both ways out (retry the same card, or change the card) live here.
import React, { useState } from "react";
import { AlertCircle, Loader2 } from "lucide-react";
import { toast } from "../../../utils/appToast";

import { formatDate, formatMoney, openRfpPaymentPortal, retryRfpPayment } from "../../../services/rfpBillingApi";

const cardLabel = (pm) => {
  if (!pm?.last4) return null;
  const brand = pm.brand ? pm.brand.charAt(0).toUpperCase() + pm.brand.slice(1) : "Card";
  return `${brand} •••• ${pm.last4}`;
};

export default function FailedPaymentCard({ subscription, canManageBilling, onRefresh }) {
  const [busy, setBusy] = useState(null); // null | "retry" | "portal"

  const {
    seat_quantity: seats,
    unit_amount: unitAmount,
    currency,
    grace_ends_at: graceEndsAt,
    payment_method: paymentMethod,
    payment_failure: failure,
  } = subscription;

  const rows = [
    ["Plan", `Premium · ${seats} user${seats === 1 ? "" : "s"}`],
    ["Amount", `${formatMoney(unitAmount * seats, currency, false)} / month`],
    cardLabel(paymentMethod) && ["Card", cardLabel(paymentMethod)],
    failure?.reason && ["Reason", failure.reason],
  ].filter(Boolean);

  const handleRetry = async () => {
    setBusy("retry");
    try {
      await retryRfpPayment();
      toast.success("Payment received — reactivating your plan.");
      // The plan flips back to active when Stripe's webhook lands, a moment after this response.
      onRefresh?.();
      [1500, 4000].forEach((ms) => setTimeout(() => onRefresh?.(), ms));
    } catch (err) {
      toast.error(err?.response?.data?.error || "Couldn't process the payment — try again.");
    } finally {
      setBusy(null);
    }
  };

  const handleChangeCard = async () => {
    setBusy("portal");
    try {
      const { portal_url: portalUrl } = await openRfpPaymentPortal(`${window.location.origin}/account/billing`);
      if (portalUrl) window.location.href = portalUrl;
    } catch {
      toast.error("Couldn't open Stripe — try again.");
      setBusy(null);
    }
  };

  return (
    <div role="alert" className="mx-auto w-full max-w-[650px] rounded-[16px] border border-[#ECE9EE] bg-white p-8">
      <div className="flex items-center gap-2 text-[15px] font-semibold text-[#B33A1E]">
        <AlertCircle size={17} /> Payment declined
      </div>
      <h3 className="mt-3 text-[28px] font-semibold tracking-[-0.02em] text-[#1A1A1A]">
        We couldn't process your payment
      </h3>
      <p className="mt-2 text-[15.5px] leading-[1.6] text-[#52525B]">
        Your bank declined the charge, so you haven't been charged. Try again or use a different card.
        {graceEndsAt && <> Your team keeps access until {formatDate(graceEndsAt)}.</>}
      </p>

      <dl className="mt-6 border-t border-[#ECE9EE]">
        {rows.map(([label, value]) => (
          <div key={label} className="flex items-center justify-between border-b border-[#ECE9EE] py-3.5 text-[15px]">
            <dt className="text-[#8A8A90]">{label}</dt>
            <dd className="font-semibold text-[#1A1A1A]">{value}</dd>
          </div>
        ))}
      </dl>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={handleRetry}
          disabled={!canManageBilling || busy !== null}
          className="flex items-center gap-2 rounded-[10px] bg-[#A04A97] px-5 py-3 text-[15px] font-semibold text-white disabled:opacity-60"
        >
          {busy === "retry" && <Loader2 size={15} className="animate-spin" />}
          {busy === "retry" ? "Trying…" : "Try again"}
        </button>
        <button
          type="button"
          onClick={handleChangeCard}
          disabled={!canManageBilling || busy !== null}
          className="flex items-center gap-2 rounded-[10px] border border-[#ECE9EE] bg-white px-5 py-3 text-[15px] font-semibold text-[#1A1A1A] disabled:opacity-60"
        >
          {busy === "portal" && <Loader2 size={15} className="animate-spin" />}
          Use a different card
        </button>
      </div>
      {!canManageBilling && (
        <p className="mt-3 text-[13.5px] text-[#8A8A90]">Ask the workspace owner to update the payment method.</p>
      )}

      <p className="mt-6 border-t border-[#ECE9EE] pt-4 text-[14px] text-[#8A8A90]">
        Need help?{" "}
        <a href="mailto:support@docusensa.com?subject=Billing%20payment%20declined" className="font-medium text-[#1A1A1A]">
          Contact support
        </a>
      </p>
    </div>
  );
}
